/* UFORIQ per-location stock limits: pure logic (no DOM, no network). Loaded before
 * assets/uq-location-stock.js and unit-tested with Node (module.exports at the bottom).
 *
 * Rule (matches the iOS app): the location that fulfills the order caps how many of a variant can be
 * in the cart.
 *   delivery + austin   -> Central Texas
 *   delivery + lubbock  -> West Texas
 *   shipping            -> whichever of Central Texas / West Texas has more (one box from one location;
 *                          order 24052 split 4 WT + 2 CT and paid shipping once, Ethan 2026-10-28)
 *   no area chosen      -> no extra limit (Shopify's normal total-stock behavior)
 * Unknown stock (no data, a failed or slow request, an untracked variant) is never a limit.
 *
 * Packs (2026-10-18): a pack variant that is a Shopify bundle of one single variant (4-Pack = 4 x Single)
 * carries the variant metafield uforiq.pack {"of": <single variant id>, "qty": 4}. Its stock is counted in
 * singles: a 4-pack in the cart uses 4 of the single's location stock, so singles + packs together never
 * go over what the location has. Without that metafield (or without the single's stock) a variant only
 * counts against itself, as before.
 */
(function (root) {
  'use strict';

  var CT = 'central-texas';
  var WT = 'west-texas';
  var LOCATIONS = {
    'gid://shopify/Location/71682195704': CT,
    'gid://shopify/Location/70666092792': WT
  };
  var AREA_LABEL = { austin: 'Austin', lubbock: 'Lubbock' };

  function normalizeArea(a) {
    a = typeof a === 'string' ? a.toLowerCase() : '';
    if (a === 'central-texas') a = 'austin';
    else if (a === 'west-texas') a = 'lubbock';
    else if (a === 'nationwide-shipping') a = 'nationwide';
    return a === 'austin' || a === 'lubbock' || a === 'nationwide' ? a : null;
  }

  /* Same rule as the scheduler's effective method: delivery needs a metro area, nationwide means shipping,
   * and a metro area with nothing chosen yet defaults to delivery. */
  function effectiveMethod(area, requested) {
    area = normalizeArea(area);
    var metro = area === 'austin' || area === 'lubbock';
    var m = requested === 'delivery' || requested === 'shipping' ? requested : null;
    if (m === 'delivery' && !metro) m = null;
    if (!m) m = metro ? 'delivery' : (area === 'nationwide' ? 'shipping' : null);
    return m;
  }

  /* -> { key: 'central-texas' | 'west-texas' | 'max', method, area } or null (no limit). */
  function resolveTarget(area, requestedMethod) {
    area = normalizeArea(area);
    if (!area) return null;
    var method = effectiveMethod(area, requestedMethod);
    if (method === 'shipping') return { key: 'max', method: 'shipping', area: area };
    if (method === 'delivery' && area === 'austin') return { key: CT, method: 'delivery', area: area };
    if (method === 'delivery' && area === 'lubbock') return { key: WT, method: 'delivery', area: area };
    return null;
  }

  function toCount(n) {
    if (n === null || n === undefined || n === '') return null;
    var x = Number(n);
    if (!isFinite(x)) return null;
    return Math.max(0, Math.floor(x));
  }

  /* Metafield value (object or JSON string) -> { 'central-texas': n|null, 'west-texas': n|null } or null. */
  function parseStock(value) {
    var v = value;
    if (typeof v === 'string') {
      try { v = JSON.parse(v); } catch (e) { return null; }
    }
    if (!v || typeof v !== 'object') return null;
    var out = {};
    out[CT] = toCount(v[CT]);
    out[WT] = toCount(v[WT]);
    if (out[CT] === null && out[WT] === null) return null;
    return out;
  }

  /* Storefront API storeAvailability nodes -> stock object, or null when there is nothing to go on.
   * Empty storeAvailability (local pickup off) means "unknown", never 0. */
  function fromStoreAvailability(nodes) {
    if (!nodes || !nodes.length) return null;
    var out = {};
    out[CT] = null;
    out[WT] = null;
    var any = false;
    nodes.forEach(function (n) {
      var key = n && n.location && LOCATIONS[n.location.id];
      var q = n ? toCount(n.quantityAvailable) : null;
      if (key && q !== null) { out[key] = q; any = true; }
    });
    return any ? out : null;
  }

  /* Storefront API ProductVariant node -> stock object or null. The metafield wins; storeAvailability
   * is only used when the metafield is missing. */
  function stockFromNode(node) {
    if (!node) return null;
    var mf = node.metafield && node.metafield.value;
    var fromMf = mf ? parseStock(mf) : null;
    if (fromMf) return fromMf;
    return fromStoreAvailability(node.storeAvailability && node.storeAvailability.nodes);
  }

  /* Metafield uforiq.pack (object or JSON string) -> { of: '<single variant id>', qty: n } or null. */
  function parsePack(value) {
    var v = value;
    if (typeof v === 'string') {
      try { v = JSON.parse(v); } catch (e) { return null; }
    }
    if (!v || typeof v !== 'object' || v.of === null || v.of === undefined) return null;
    var qty = toCount(v.qty);
    var of = String(v.of).replace(/^gid:\/\/shopify\/ProductVariant\//, '');
    if (!qty || qty < 1 || !/^\d+$/.test(of)) return null;
    return { of: of, qty: qty };
  }

  /* -> number (the cap) or null (no limit). */
  function limitFor(stock, target) {
    if (!target || !stock) return null;
    if (target.key === 'max') {
      var a = stock[CT], b = stock[WT];
      if (a === null && b === null) return null;
      return Math.max(a || 0, b || 0);
    }
    var q = stock[target.key];
    return q === null || q === undefined ? null : q;
  }

  /* Copy must match the iOS app exactly. */
  function message(target, n) {
    if (!target) return '';
    if (target.method === 'shipping') return 'Only ' + n + ' available to ship.';
    return 'Only ' + n + ' available for delivery in the ' + AREA_LABEL[target.area] + ' area.';
  }

  /* How many more of a variant can be added. -> { limit, inCart, remaining, allowed } where `allowed` is
   * what to add for a request of `requested` (null limit = no cap). */
  function capAdd(requested, inCart, limit) {
    requested = Math.max(0, Math.floor(Number(requested) || 0));
    inCart = Math.max(0, Math.floor(Number(inCart) || 0));
    if (limit === null || limit === undefined) {
      return { limit: null, inCart: inCart, remaining: null, allowed: requested, capped: false };
    }
    var remaining = Math.max(0, limit - inCart);
    var allowed = Math.min(requested, remaining);
    return { limit: limit, inCart: inCart, remaining: remaining, allowed: allowed, capped: allowed < requested };
  }

  /* Cart lines -> per-line verdicts. A variant can sit on several lines (different properties); the cap
   * applies to their total, and "Reduce to N" on a line keeps the other lines as they are.
   * lines: [{ key, variantId, qty, addon }]
   * stockOf(variantId) -> stock object or null
   * -> [{ key, variantId, qty, limit, maxForLine, over, reduceTo, message }] */
  /* Which stock a variant counts against: { base: variant id, mult: singles per unit }. A pack counts
   * against its single when the single's stock is known for the target; anything else against itself. */
  function unitFor(variantId, stockOf, target, packOf) {
    var p = packOf ? packOf(variantId) : null;
    if (p && limitFor(stockOf(p.of), target) !== null) return { base: String(p.of), mult: p.qty };
    return { base: String(variantId), mult: 1 };
  }

  /* packOf(variantId) -> { of, qty } or null (optional). */
  function evaluateLines(lines, stockOf, target, packOf) {
    var totals = {};
    var packed = {};
    var left = {};
    var units = (lines || []).map(function (l) { return l.addon ? null : unitFor(l.variantId, stockOf, target, packOf); });
    (lines || []).forEach(function (l, i) {
      var u = units[i];
      if (!u) return;
      totals[u.base] = (totals[u.base] || 0) + (Number(l.qty) || 0) * u.mult;
      if (u.mult > 1) packed[u.base] = true;
    });
    return (lines || []).map(function (l, i) {
      var u = units[i];
      var qty = Number(l.qty) || 0;
      var limit = u ? limitFor(stockOf(u.base), target) : null;
      if (limit === null) return { key: l.key, variantId: l.variantId, qty: qty, limit: null, maxForLine: null, over: false, reduceTo: null, message: '' };
      var b = u.base;
      // How high this line could go with the other lines as they are (the + stepper's ceiling).
      var maxForLine = Math.max(0, Math.floor((limit - (totals[b] - qty * u.mult)) / u.mult));
      // Over-limit lines: earlier lines keep their quantity, later lines get what is left.
      if (left[b] === undefined) left[b] = limit;
      var keep = Math.min(qty, Math.floor(left[b] / u.mult));
      left[b] -= keep * u.mult;
      var over = qty > keep;
      // With packs in play the number to show is what this line can have, not the location's singles.
      var shown = packed[b] ? maxForLine : limit;
      return {
        key: l.key, variantId: l.variantId, qty: qty, limit: shown, maxForLine: maxForLine, over: over,
        reduceTo: over ? keep : null,
        message: message(target, shown)
      };
    });
  }

  function anyOver(verdicts) { return (verdicts || []).some(function (v) { return v.over; }); }

  function variantGid(id) {
    var s = String(id);
    return /^gid:\/\//.test(s) ? s : 'gid://shopify/ProductVariant/' + s.replace(/\D/g, '');
  }
  function variantNumericId(gid) {
    var m = String(gid).match(/(\d+)$/);
    return m ? m[1] : String(gid);
  }

  var api = {
    CT: CT,
    WT: WT,
    LOCATIONS: LOCATIONS,
    normalizeArea: normalizeArea,
    effectiveMethod: effectiveMethod,
    resolveTarget: resolveTarget,
    parseStock: parseStock,
    fromStoreAvailability: fromStoreAvailability,
    stockFromNode: stockFromNode,
    limitFor: limitFor,
    parsePack: parsePack,
    unitFor: unitFor,
    message: message,
    capAdd: capAdd,
    evaluateLines: evaluateLines,
    anyOver: anyOver,
    variantGid: variantGid,
    variantNumericId: variantNumericId
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.UqStockLogic = api;
})(typeof window !== 'undefined' ? window : this);
